import * as React from "react";
import { ImagePlus, Upload } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@mebi/ui";
import { useImageUpload } from "@/hooks/useImageUpload";
import UploadSourceModal from "@/components/ui/UploadSourceModal";
import { getR2PublicUrl } from "@/storage/uploadService";

export interface BannerUploadProps {
  /**
   * Variant of the upload component
   */
  variant?: "default" | "payment";
  /**
   * Current banner URL or placeholder
   */
  bannerUrl?: string;

  /**
   * Function to handle when banner is changed
   */
  onBannerChange?: (
    file: File,
    previewUrl: string,
    errorMessage?: string,
  ) => void;

  /**
   * Function called when removing banner
   */
  onBannerRemove?: () => void;

  /**
   * Whether the banner is editable or not
   */
  editable?: boolean;

  /**
   * Class name to be applied to the outer container
   */
  className?: string;

  /**
   * Additional title text to display
   */
  title?: string;

  /**
   * Maximum file size in MB
   */
  maxSizeMB?: number;

  /**
   * Upload in progress
   */
  isUploading?: boolean;

  /**
   * Allow remove banner
   */
  allowRemove?: boolean;

  /**
   * Success state
   */
  isSuccess?: boolean;

  /**
   * Error message to display
   */
  errorMessage?: string;
}

/**
 * BannerUpload component - Upload banner cho tournament với MEBI Design System
 *
 * @example
 * ```tsx
 * <BannerUpload
 *   title="Ảnh banner giải đấu"
 *   onBannerChange={handleBannerChange}
 *   allowRemove={true}
 *   maxSizeMB={10}
 * />
 * ```
 */
const BannerUpload = React.forwardRef<HTMLDivElement, BannerUploadProps>(
  (
    {
      variant = "default",
      bannerUrl = "",
      onBannerChange,
      onBannerRemove,
      editable = true,
      className,
      title,
      maxSizeMB = 10,
      isUploading = false,
      allowRemove = false,
      isSuccess = false,
      errorMessage,
      ...props
    },
    ref,
  ) => {
    const resolveBannerUrl = React.useCallback((url: string) => {
      const normalized = url.trim();
      if (!normalized) {
        return "";
      }

      if (
        /^(data:|blob:|https?:|file:\/\/|content:\/\/|capacitor:\/\/)/i.test(
          normalized,
        )
      ) {
        return normalized;
      }

      if (normalized.startsWith("/assets/")) {
        return normalized;
      }

      return getR2PublicUrl(normalized) || normalized;
    }, []);

    const {
      previewUrl,
      isOpen,
      setIsOpen,
      internalError,
      fileInputRef,
      openCamera,
      openGallery,
      acceptedTypes,
      handleFileInputChange,
      handleRemove,
    } = useImageUpload({
      initialUrl: bannerUrl,
      maxSizeMB,
      editable,
      isUploading,
      onChange: onBannerChange,
      onRemove: onBannerRemove,
      resolveUrl: resolveBannerUrl,
    });

    const displayError = errorMessage || internalError;

    const handleCameraSelect = React.useCallback(() => {
      openCamera();
      setIsOpen(false);
    }, [openCamera, setIsOpen]);

    const handleGallerySelect = React.useCallback(() => {
      openGallery();
      setIsOpen(false);
    }, [openGallery, setIsOpen]);

    const getDefaultBanner = () => {
      return "https://placehold.co/354x185/F0F0F0/CCCCCC";
    };

    return (
      <div ref={ref} className={cn("relative w-full", className)} {...props}>
        {/* Title */}
        {title && (
          <div className="mb-3">
            <p className="mebi-body-small-semibold text-neutral-1300">
              {title}
            </p>
          </div>
        )}

        {/* Banner container */}
        <div className="relative w-full">
          <div
            className={cn(
              "relative w-full h-[185px] overflow-hidden rounded-2xl border-2 transition-all duration-200",
              variant === "default" ? "bg-neutral-100" : "bg-black",
              {
                "border-mebi-yellow-500": editable && !errorMessage,
                "border-mebi-red-500": errorMessage,
                "border-mebi-green-500": isSuccess,
                "border-neutral-300": !editable,
                "cursor-pointer hover:border-mebi-yellow-600":
                  editable && !isUploading,
                "cursor-not-allowed opacity-60": !editable || isUploading,
              },
            )}
          >
            <img
              src={previewUrl || getDefaultBanner()}
              alt="Tournament Banner"
              className="w-full h-full object-cover"
              onError={(e) => {
                e.currentTarget.src = getDefaultBanner();
              }}
            />

            {/* Loading overlay */}
            {isUploading && (
              <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {/* Upload overlay for editable */}
            {editable && !isUploading && !previewUrl && (
              <button
                type="button"
                className="absolute inset-0 bg-black/0 hover:bg-black/10 transition-colors duration-200 flex items-center justify-center group"
                onClick={() => setIsOpen(true)}
              >
                <div className="text-center">
                  {variant === "default" ? (
                    <>
                      <ImagePlus className="w-8 h-8 text-neutral-500 mx-auto mb-2" />
                      <p className="text-neutral-600 text-sm font-medium">
                        Thêm ảnh banner
                      </p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-8 h-8 text-mebi-yellow-500 mx-auto mb-2" />
                      <p className="text-mebi-yellow-500 text-sm font-medium">
                        Tải ảnh lên từ thiết bị
                      </p>
                    </>
                  )}
                </div>
              </button>
            )}

            {/* Upload button - centered */}
            {editable && !isUploading && previewUrl && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Button
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsOpen(true);
                  }}
                  size="sm"
                  variant="secondary"
                  className="opacity-90 hover:opacity-100"
                  leftIcon={<ImagePlus className="w-4 h-4" />}
                >
                  Thay đổi ảnh
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Error message */}
        {displayError && (
          <p className="mt-2 mebi-body-xsmall-regular text-mebi-red-600">
            {displayError}
          </p>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={acceptedTypes.join(",")}
          onChange={handleFileInputChange}
          className="sr-only"
        />

        <UploadSourceModal
          open={isOpen}
          onOpenChange={setIsOpen}
          title="Thêm tệp"
          description={
            variant === "default"
              ? "Lựa chọn cách thức cập nhật ảnh banner: chụp ảnh mới hoặc chọn từ thư viện."
              : "Lựa chọn cách thức cập nhật ảnh chứng từ: chụp ảnh mới hoặc chọn từ thư viện."
          }
          cameraLabel="Chụp ảnh mới"
          galleryLabel="Chọn từ thư viện"
          onCamera={handleCameraSelect}
          onGallery={handleGallerySelect}
          showRemove={allowRemove && Boolean(previewUrl)}
          removeLabel="Xóa ảnh banner"
          onRemove={handleRemove}
        />
      </div>
    );
  },
);

BannerUpload.displayName = "BannerUpload";

export { BannerUpload };
